import PropTypes from 'prop-types';
import { Fragment, useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import {
    CompletedStates, isCustomerActionsStateOrBefore, isVirWaitingToFile, isReviewTask, WorkflowTasks
} from '../../../utils/WorkflowTasks';
import '../Section/section.css';
import DocumentUploadReturnSection from './DocumentUploadReturnSection';
import DownloadReturnSection from './DownloadReturnSection';
import InProgressBlankStateReturnSection from './InProgressBlankStateReturnSection';
import InterviewReturnSection from './InterviewReturnSection';
import ReviewAndApproveReturnSection from './ReviewAndApproveReturnSection';
import PaymentSection from '../Payment/PaymentSection'
import { trackEvent, HeapEvents } from '../../../utils/AnalyticsUtil';
import SurveyLinkSection from './SurveyLinkSection'
import SignAuthorizationRequest from '../AdditionalDocuments/SignAuthorizationRequest'
import { shouldShowSignatureSection, isInPaymentState } from '../../../utils/WorkflowUtil';

const TaxYearSection = ({ currentYear, taxState, lang, onWorkflowButtonAction, securityComplete, paymentInfo, section }) => {
    const [tasks, setTasks] = useState([])
    const [hasReviewTask, setHasReviewTask] = useState(false)
    const [isCompleted, setIsCompleted] = useState(false)
    const [isVirWaitingToFileState, setIsVirWaitingToFileState] = useState(false)
    const [isPaymentState, setIsPaymentState] = useState(false);
    const trackedEventsRef = useRef({});
    const [showSurvey, setShowSurvey] = useState(true);
    const assigmentState = taxState?.assigned_tax_pro;
    const currentPage = taxState?.interview_state?.current_page;
    const isDirect = taxState?.is_direct;
    const previouslyCompleted = taxState?.interview_state.completed_date !== null && !taxState.interview_state.complete;

    const pendingTasks = () => {
        if(!taxState?.ticketTasks)
        {
            return [];
        }

        return taxState.ticketTasks.filter(x => x.target === '1' && !x.isCompleted);
    }
    const isPending = (tasks) => tasks.length > 0;
    const isPaymentPending = () => {
        if(!taxState?.ticketTasks)
        {
            return false;
        }

        return isPending(taxState.ticketTasks.filter(x => x.taskId === WorkflowTasks.PAYMENT_RECEIVED && !x.isCompleted));
    }

    const pendingWorkflowTasks = pendingTasks();

    useEffect(() => {
        setTasks(pendingWorkflowTasks);
        setHasReviewTask(pendingWorkflowTasks.filter(x => isReviewTask(x.taskId)).length > 0)
        setIsCompleted(taxState && CompletedStates.includes(taxState.ticketState))
        setIsVirWaitingToFileState(isVirWaitingToFile(taxState.ticketState))
        setIsPaymentState(isInPaymentState(taxState));
        
        // Backend calculates if survey should be shown (90 days from payment completion)
        setShowSurvey(taxState.show_survey || false);
        
        trackStatusComplete();
    }, [taxState])

    const trackEventOnce = (eventName) => {
        const caseId = taxState?.workflow_state?.case_id;
        if (!caseId) return;

        const eventKey = `${caseId}_${eventName}`;
        if (!trackedEventsRef.current[eventKey]) {
            trackedEventsRef.current[eventKey] = true;
            trackEvent(eventName);
        }
    };
   
    const trackStatusComplete = () => {
        const hasDocumentUploadPending = isPending(pendingWorkflowTasks.filter(x => x.taskId === WorkflowTasks.DOCS_UPLOADED && !x.isCompleted));
        const hasReviewPending = isPending(pendingWorkflowTasks.filter(x => isReviewTask(x.taskId) && !x.isCompleted));
        const hasPaymentPending = isPaymentPending();

        if(section === 'documents' && !hasDocumentUploadPending) {
            trackEventOnce(HeapEvents.STATUS_DOC_UPLOAD_COMPLETE);
        }
        else if(section === 'review' && !hasReviewPending) {
            trackEventOnce(HeapEvents.STATUS_CLIENT_REVIEW_COMPLETE);
        }
        else if(section === 'payment' || section === 'payment_qc' || section === 'manual_payment') {
            const isManualPayment = !paymentInfo || paymentInfo.length == 0;
            if(!hasPaymentPending) {
                trackEventOnce(HeapEvents.STATUS_PAYMENT_COMPLETE);
            }
            else if(isManualPayment) {
                trackEventOnce(HeapEvents.STATUS_PAYMENT_MANUAL_PAYMENT_REQUEST);

            }
        }
    }

    // Interview tile shouldn't be displayed at first load of dashboard after registration/before starting the interview
    if (assigmentState === null) {
        return null;
    }

    return (
        <Fragment>
            {
                (tasks.filter(x => x.taskId === WorkflowTasks.INTERVIEW_COMPLETED).length > 0) && ((isDirect && currentPage !== "1") || !isDirect || previouslyCompleted) ?
                    <InterviewReturnSection taxState={taxState} currentYear={currentYear} onWorkflowButtonAction={onWorkflowButtonAction} /> : ''
            }
            {
                tasks.filter(x => x.taskId === WorkflowTasks.DOCS_UPLOADED).length > 0 &&
                <DocumentUploadReturnSection taxState={taxState} currentYear={currentYear}
                    onWorkflowButtonAction={onWorkflowButtonAction} />
            }
              
            {
                shouldShowSignatureSection(taxState, section) &&
                <SignAuthorizationRequest
                    taxState={taxState}
                    lang={lang}
                    currentYear={currentYear}
                    onWorkflowButtonAction={onWorkflowButtonAction}
                />
            }
            {
                // WayFinding: Show completed state for each section when no pending tasks
                (section === 'documents' || section === 'review' || section === 'payment' || section === 'payment_qc') && !hasReviewTask && !isPaymentState && tasks.length === 0 && securityComplete &&
                <InProgressBlankStateReturnSection taxState={taxState} isWaitingToFile={isVirWaitingToFileState} section={section} />
            }
            {
                section === 'review' && hasReviewTask &&
                <ReviewAndApproveReturnSection taxState={taxState} currentYear={currentYear} securityComplete={securityComplete} onWorkflowButtonAction={onWorkflowButtonAction} />
            }
            {
                isPaymentState &&
                <PaymentSection lang={lang} currentYear={currentYear} securityComplete={securityComplete}
                    taxState={taxState} paymentInfo={paymentInfo} onWorkflowButtonAction={onWorkflowButtonAction} />
            }
            {
                isCompleted &&
                <Fragment>
                    <DownloadReturnSection taxState={taxState} onWorkflowButtonAction={onWorkflowButtonAction} section={section} />
                    {showSurvey && <SurveyLinkSection taxState={taxState} />}
                </Fragment>
            }
        </Fragment>
    );
}

TaxYearSection.propTypes = {
    taxState: PropTypes.shape({
        taxYear: PropTypes.number.isRequired,
        ticketState: PropTypes.string.isRequired,
        ticketTasks: PropTypes.arrayOf(PropTypes.object),
        coupled: PropTypes.bool.isRequired,
        primary_province: PropTypes.string,
        primary_firstName: PropTypes.string.isRequired,
        spouse_province: PropTypes.string,
        spouse_firstName: PropTypes.string,
        subTotal: PropTypes.number,
        tenderTotal: PropTypes.number
    }),
    securityComplete: PropTypes.bool.isRequired,
    currentYear: PropTypes.number.isRequired,
    onWorkflowButtonAction: PropTypes.func.isRequired,
    lang: PropTypes.string.isRequired
}

const mapStateToProps = state => ({
    lang: state.layout.lang
});

export default connect(mapStateToProps)(TaxYearSection);
